export default defineNuxtRouteMiddleware(async (to, from) => {
  if (to.path === "/_ready") return;
  if (to.path.startsWith("/notfound")) return;

  const { site } = useSite();
  const isDisabledPath = to.path.startsWith("/disabled");
  const shouldRedirectToDisabled = () =>
    site.value?.flags?.["site.disabled"] === true && !isDisabledPath;

  if (site.value) {
    if (shouldRedirectToDisabled()) {
      return navigateTo("/disabled");
    }

    return;
  }

  const rawHost =
    useRequestHeader("x-original-host") ||
    useRequestHeader("host") ||
    useRequestURL().host;
  // Strip port so the hostname matches the SiteMap entry (which is stored without port)
  const host = rawHost?.replace(/:\d+$/, "");
  if (!host) {
    return navigateTo("/notfound");
  }
  const {
    bootstrapApiUrl,
    publicApiUrl,
    customApiUrlForHost,
    configureForHost,
  } = useApiUrl();

  const siteIdCookie = useCookie<string | null>("candidate-site-id", {
    // cookie must be readable by client middleware after SSR
    path: "/",
    httpOnly: false,
  });

  let siteId: string | null = null;
  const queryId = to.query.siteid as string | undefined;
  if (queryId) {
    siteIdCookie.value = queryId;
    siteId = queryId;
  } else {
    siteId = siteIdCookie.value;
  }

  try {
    let siteConfiguration: any;
    if (siteId) {
      siteConfiguration = await $fetch(
        `${bootstrapApiUrl}/v1/candidate/site/by/id`,
        {
          allowAnonymous: true,
          headers: {
            "x-site-id": siteId,
            "x-include-domain-contracts": "settings,flags",
          },
        }
      );
    } else {
      siteConfiguration = await $fetch(
        `${bootstrapApiUrl}/v1/candidate/site/by/url`,
        {
          allowAnonymous: true,
          headers: {
            "x-site-url": host as string,
            "x-include-domain-contracts": "settings,flags",
          },
        }
      );
    }

    site.value = siteConfiguration;
    const customApiUrl = customApiUrlForHost(host);
    let customApiReady = false;
    if (customApiUrl) {
      try {
        const aliasConfiguration = await $fetch<any>(
          `${customApiUrl}/v1/candidate/site/by/url`,
          {
            allowAnonymous: true,
            timeout: 2000,
            headers: {
              "x-site-url": host,
              "x-include-domain-contracts": "settings,flags",
            },
          }
        );
        customApiReady = aliasConfiguration?.id === siteConfiguration.id;
      } catch (error) {
        console.warn(`Custom API alias is not ready for ${host}`, error);
      }
    }
    configureForHost(host, customApiReady);

    useHead({
      title: site.value?.name || "Candidate Portal",
      meta: [
        {
          name: "description",
          content:
            site.value?.settings?.description ||
            "Candidate portal for job applications.",
        },
      ],
    });

    // ── Theme: set URLs + inline CSS for instant SSR ──
    const themeBase = `${publicApiUrl.value}/v1/file/${siteConfiguration.id}/download/theme`;

    useState<string | null>("site-css", () => null).value = `${themeBase}/themecss`;
    useState<string | null>("site-logo", () => null).value = `${themeBase}/logosvg`;
    useState<string | null>("site-logo-svg", () => null).value = `${themeBase}/logosvg`;
    useState<string | null>("site-logo-png", () => null).value = `${themeBase}/logopng`;
    useState<string | null>("site-logo-jpg", () => null).value = `${themeBase}/logojpg`;
    useState<string | null>("site-logo-jpeg", () => null).value = `${themeBase}/logojpeg`;
    useState<string | null>("site-background", () => null).value = `${themeBase}/backgroundsvg`;

    // Inline the theme CSS during SSR so the first paint uses the correct tokens
    if (import.meta.server) {
      try {
        const css = await $fetch<string>(
          `${bootstrapApiUrl}/v1/file/${siteConfiguration.id}/download/theme/themecss`,
          {
            responseType: "text",
            headers: { "x-site-id": siteConfiguration.id },
          },
        );
        if (css && typeof css === "string") {
          const safeCss = css.replace(/<\/style/gi, "<\\/style");
          useHead({
            style: [{ key: "site-theme", innerHTML: safeCss }],
          });
        }
      } catch {
        // Theme CSS doesn't exist yet — default theme will apply
      }
    }

    // Favicon (SSR-visible)
    useHead({
      link: [
        { rel: "icon", type: "image/svg+xml", href: `${themeBase}/faviconsvg`, key: "favicon-svg" },
        { rel: "icon", type: "image/png", href: `${themeBase}/faviconpng`, key: "favicon-png" },
      ],
    });

    if (shouldRedirectToDisabled()) {
      return navigateTo("/disabled");
    }
  } catch (error) {
    console.error("Error fetching site configuration:", error);
    return navigateTo("/notfound");
  }
});
